// ===========================================
// Lesson Pieces Button Component
// ===========================================
// Shows a "Pieces" button for the Teacher Tasks page
// Opens ClassPieceProgressModal for teacher piece progress marking

import { useState } from 'react';
import { Button, Skeleton } from '@mui/material';
import { MusicNote as MusicNoteIcon } from '@mui/icons-material';
import ClassPieceProgressModal from './ClassPieceProgressModal';
import { useLessonPieces } from '../../hooks/useGamification';
import type { LessonPiece } from '../../types/gamification.types';

// ===========================================
// TYPES
// ===========================================

interface LessonPiecesButtonProps {
  lessonId: string;
  lessonName: string;
}

// ===========================================
// COMPONENT
// ===========================================

export default function LessonPiecesButton({
  lessonId,
  lessonName,
}: LessonPiecesButtonProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const { data: lessonPieces, isLoading } = useLessonPieces(lessonId);

  const currentPieces: LessonPiece[] = (lessonPieces || []).filter(
    (lp: LessonPiece) => lp.isCurrent
  );
  const hasCurrentPieces = currentPieces.length > 0;

  if (isLoading) {
    return <Skeleton variant="rectangular" width={90} height={30} sx={{ borderRadius: 1 }} />;
  }

  return (
    <>
      <Button
        size="small"
        variant={hasCurrentPieces ? 'contained' : 'outlined'}
        startIcon={<MusicNoteIcon />}
        onClick={() => setModalOpen(true)}
        sx={hasCurrentPieces ? {
          bgcolor: 'rgba(255,174,158,0.2)',
          color: '#c4685a',
          boxShadow: 'none',
          '&:hover': { bgcolor: 'rgba(255,174,158,0.35)', boxShadow: 'none' },
        } : {
          borderColor: '#FFAE9E',
          color: '#c4685a',
          '&:hover': { borderColor: '#ff9a87', bgcolor: 'rgba(255,174,158,0.08)' },
        }}
      >
        Pieces{hasCurrentPieces ? ` (${currentPieces.length})` : ''}
      </Button>

      <ClassPieceProgressModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        lessonId={lessonId}
        lessonName={lessonName}
        lessonPieces={currentPieces}
      />
    </>
  );
}
